import React from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import IconButton from '@material-ui/core/IconButton';
import RemoveCircle from '@material-ui/icons/RemoveCircle';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckIcon from '@material-ui/icons/Check';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import WarningIcon from '@material-ui/icons/Warning';
import {MembershipModes} from "../subscription-assignment/SubscriptionAssignmentEditor";
import {FormStatus} from "../../models/shared";
import {SubscriptionType} from "../../models/subscription-model";
import {TrimmedTextField} from "../_shared/TrimmedTextField";
import withFormItemComponentStyle from "../ui/styles/StyledFormItemComponent";
import {LanguageSelector} from "../_shared/LanguageSelector";

const SubscriptionAssignmentItem =
    ({
         children,
         submitted,
         form,
         handleFormChange,
         handleFormDeleted,
         classes
     }: {
        children: React.FC | null,
        submitted: boolean,
        form: any,
        handleFormChange: (name: any, value: any, form: any) => void,
        handleFormDeleted: (id: any) => void,
        classes: any
    }) => {
        const {t} = useTranslation();
        const {data, availableTypes, errors} = form;
        const lockInput = form.status === FormStatus.processing || form.status === FormStatus.successful;

        const handleInputChange = (e: any) => {
            e.preventDefault();
            e.stopPropagation();
            const {value, name} = e.target;
            handleFormChange(name, value, form);
        };

        const handleDeleteItem = (e: any) => {
            e.preventDefault();
            e.stopPropagation();
            handleFormDeleted(form.id);
        };

        const hasError = (field: any) => {
            return form.status === 'failed' || (submitted && !!errors[field])
        };

        const getTranslatedError = (errorEnum: any) => {
            if (!errorEnum) return null;
            return t(`_Common.Messages.${errorEnum}`);
        };

        let action = <IconButton aria-label="delete" className={classes.deleteBtn} onClick={handleDeleteItem}>
            <RemoveCircle/>
        </IconButton>;

        if (form.status === FormStatus.processing)
            action = <CircularProgress size={20} color="inherit"/>;
        else if (form.status === FormStatus.successful)
            action = <CheckIcon className={classes.successLabel}/>;
        else if (form.status === FormStatus.failed)
            action = <WarningIcon color='error'/>;


        return <Grid className={classes.root}>
            <FormControl
                disabled={lockInput || form.membershipMode === MembershipModes.MembershipOnly}
                className={classes.dropdown}
                error={hasError('subscriptionType')}>
                <InputLabel>{t('_Common.Labels.LicenseType')}</InputLabel>
                <Select
                    name='subscriptionType'
                    value={data.subscriptionType}
                    onChange={handleInputChange}
                >
                    {availableTypes.some((type: SubscriptionType) => type === SubscriptionType.Standard) &&
                    <MenuItem value={1}>Standard</MenuItem>}
                    {availableTypes.some((type: SubscriptionType) => type === SubscriptionType.Provider15) &&
                    <MenuItem value={3}>Pro</MenuItem>}
                </Select>
                <FormHelperText>{hasError('subscriptionType') ? getTranslatedError(errors.subscriptionType) : null}</FormHelperText>
            </FormControl>
            <LanguageSelector
                disabled={lockInput}
                name='language'
                error={hasError('language')}
                value={data.language}
                onFormChange={handleInputChange}
                label={t('_Common.Labels.Language')}
                className={classes.dropdown}
                helperText={hasError('language') ? getTranslatedError(errors.language) : null}
            />
            <TrimmedTextField
                disabled={lockInput}
                name='email'
                error={hasError('email')}
                value={data.email}
                onFormChange={(name: string, value: string) => handleFormChange(name, value, form)}
                label={t('_Common.Labels.Email')}
                helperText={hasError('email') ? getTranslatedError(errors.email) : null}
            />
            <TrimmedTextField
                disabled={lockInput}
                name='firstName'
                error={hasError('firstName')}
                value={data.firstName}
                onFormChange={(name: string, value: string) => handleFormChange(name, value, form)}
                label={t('_Common.Labels.FirstName')}
                helperText={hasError('firstName') ? getTranslatedError(errors.firstName) : null}
            />
            <TrimmedTextField
                disabled={lockInput}
                name='lastName'
                error={hasError('lastName')}
                value={data.lastName}
                onFormChange={(name: string, value: string) => handleFormChange(name, value, form)}
                label={t('_Common.Labels.LastName')}
                helperText={hasError('lastName') ? getTranslatedError(errors.lastName) : null}
            />
            <div className={classes.actionContainer}>
                {
                    action
                }
            </div>
            {
                children ?

                    <ExpansionPanel className={classes.errorsContainer} defaultExpanded={true}>
                        <ExpansionPanelSummary
                            expandIcon={<ExpandMoreIcon/>}
                            aria-controls="panel1a-content"
                            id="panel1a-header">
                            <WarningIcon color='error'/>
                            <Typography
                                variant='caption'>{t('Subscription.SubscriptionAssignment.EntryNeedsAttention')}</Typography>
                        </ExpansionPanelSummary>
                        <ExpansionPanelDetails>
                            {children}
                        </ExpansionPanelDetails>
                    </ExpansionPanel>
                    : null
            }
        </Grid>
    };

export const StyledSubscriptionAssignmentItem = withFormItemComponentStyle(SubscriptionAssignmentItem);
